// AutomationsPage — table of automation routes grouped into sections. Visual
// preserved; content sourced from page_content row at slug `system-automations`.

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/lib/api";
import { cn } from "@/lib/utils";

type EntryRow = { path: string; title: string; subtitle?: string };
type EntrySection = { eyebrow?: string; rows: EntryRow[] };

type AutomationsContent = {
  kind: "link-table-v1";
  header: { eyebrow?: string; title: string; lede?: string };
  sections: EntrySection[];
};

const SLUG = "system-automations";

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-svh -mx-6 -my-8 px-6 py-6 sm:py-8 bg-stone-50">
      <div className="mx-auto max-w-5xl">{children}</div>
    </div>
  );
}

function EntryTable({
  rows,
  navigate,
}: {
  rows: EntryRow[];
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <div className="rounded-[24px] bg-white overflow-hidden">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r, idx) => (
            <tr
              key={r.path}
              onClick={() => navigate(r.path)}
              className={cn(
                "border-t first:border-t-0 border-stone-200/70 cursor-pointer",
                "hover:bg-stone-50 transition-colors",
              )}
            >
              <td className="px-6 py-4 align-middle font-bold tabular-nums text-stone-900 w-16">
                {String(idx + 1).padStart(2, "0")}
              </td>
              <td className="px-6 py-4 align-middle">
                <span className="text-base font-semibold text-stone-900 tracking-tight">
                  {r.title}
                </span>
                {r.subtitle && (
                  <p className="mt-1 text-sm leading-relaxed text-stone-600">
                    {r.subtitle}
                  </p>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AutomationsPage() {
  const navigate = useNavigate();
  const api = useApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ["kb", SLUG],
    queryFn: async (): Promise<AutomationsContent> => {
      const res = await api(`/api/kb/${SLUG}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { content: AutomationsContent };
      return json.content;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <PageFrame>
        <div className="flex items-center gap-2 p-2 text-sm text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          טוען…
        </div>
      </PageFrame>
    );
  }

  if (error || !data) {
    return (
      <PageFrame>
        <div className="rounded-2xl border border-rose-300/60 bg-rose-50/40 p-5">
          <div className="text-sm font-semibold text-rose-700">לא ניתן לטעון את העמוד</div>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <header className="mb-8">
        {data.header.eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
            {data.header.eyebrow}
          </p>
        )}
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-stone-900">
          {data.header.title}
        </h1>
        {data.header.lede && (
          <p className="mt-3 text-base text-stone-600 max-w-xl leading-relaxed">
            {data.header.lede}
          </p>
        )}
      </header>

      <div className="space-y-12">
        {data.sections.map((sec, i) => (
          <section key={i}>
            {sec.eyebrow && (
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500 mb-3">
                {sec.eyebrow}
              </p>
            )}
            <EntryTable rows={sec.rows} navigate={navigate} />
          </section>
        ))}
      </div>
    </PageFrame>
  );
}
